import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack, Typography, Avatar, Chip, CircularProgress } from "@mui/material";
import { Person, SportsSoccer } from "@mui/icons-material";
import { getPlayersRelatedToTeam } from "../config/mainRequest";

// ─── Match Summary ───
// Mirrors the TMS Match Summary (MatchSummaryNew.js) integration:
//  • Scoreline + Overall Summary read from match.match_sense_data (matchById
//    response): per-team entries in .data keyed by team_notation "A"/"B".
//  • Goal/card events from each team's special_actions vector_map.
//  • Lineups from playersRelatedToTeam (user_match_details holds jersey,
//    game_time, preferred_position).

const TEAM_A_COLOR = "#4c6ef5"; // left team bar
const TEAM_B_COLOR = "rgba(232, 108, 0, 1)"; // right team bar

// Same position id → alias mapping as TMS
const POSITION_LIST = [
  { id: 1, alias: "GK" },
  { id: 2, alias: "CB" },
  { id: 3, alias: "LB" },
  { id: 4, alias: "RB" },
  { id: 5, alias: "LWB" },
  { id: 6, alias: "RWB" },
  { id: 7, alias: "CDM" },
  { id: 8, alias: "CM" },
  { id: 9, alias: "CAM" },
  { id: 10, alias: "LM" },
  { id: 11, alias: "RM" },
  { id: 12, alias: "LW" },
  { id: 13, alias: "RW" },
  { id: 14, alias: "LF" },
  { id: 15, alias: "RF" },
  { id: 16, alias: "CF" },
  { id: 17, alias: "ST" },
];
const positionAlias = (id) => POSITION_LIST.find((p) => p.id === id)?.alias || "NA";

const startCase = (s) =>
  String(s || "")
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

// Goal/card timeline from special_actions, exactly as TMS specialActionData():
// own goals are displayed under the opposing team; rows sorted by video time.
const specialActionData = (matchSense, team) => {
  if (!matchSense) return [];
  const typesToInclude = ["own_goal", "goal", "yellow_card", "red_card"];
  const filteredActions = [];

  ["A", "B"].forEach((notation) => {
    const teamData = matchSense?.data?.find((i) => i.team_notation === notation);
    if (!teamData?.special_actions) return;
    Object.keys(teamData.special_actions)
      .filter((key) => typesToInclude.includes(key))
      .forEach((key) => {
        (teamData.special_actions[key]?.vector_map || []).forEach((action) => {
          const displayTeam =
            key === "own_goal" ? (notation === "A" ? "B" : "A") : notation;
          if (team === displayTeam) {
            filteredActions.push({ ...action, type: key, originalTeam: notation });
          }
        });
      });
  });

  filteredActions.sort((a, b) => {
    const [hA = 0, mA = 0, sA = 0] = (a?.starting_video || "0:0:0").split(":").map(Number);
    const [hB = 0, mB = 0, sB = 0] = (b?.starting_video || "0:0:0").split(":").map(Number);
    return hA * 60 + mA + sA / 60 - (hB * 60 + mB + sB / 60);
  });

  return filteredActions.map((action) => ({
    name: startCase(action?.user_name),
    type: action.type,
    time: action?.normalized_time,
    playerId: action?.user_id,
  }));
};

// ─── Event icon (goal / own goal / red / yellow) ───
const EventIcon = ({ type }) => {
  if (type === "goal")
    return <SportsSoccer sx={{ fontSize: 15, color: "#4c6ef5" }} />;
  if (type === "own_goal")
    return <SportsSoccer sx={{ fontSize: 15, color: "#e03131" }} />;
  return (
    <Box
      sx={{
        width: 10,
        height: 14,
        borderRadius: "2px",
        backgroundColor: type === "red_card" ? "#e03131" : "#fab005",
        flexShrink: 0,
      }}
    />
  );
};

const LEGEND = [
  { type: "goal", label: "Goal" },
  { type: "own_goal", label: "Own Goal" },
  { type: "red_card", label: "Red Card" },
  { type: "yellow_card", label: "Yellow Card" },
];

// ─── One comparison row in Overall Summary ───
// Same rules as TMS CompareAttribsV2: hide when either value is missing,
// round displayed values (except PPDA), higher wins — except PPDA where
// lower wins; ties highlight both sides.
const StatRow = ({ label, a, b }) => {
  if (a === undefined || b === undefined || a === null || b === null) return null;
  const isPPDA = label === "PPDA";
  const lowerWins = isPPDA || label === "Fouls";
  const isDecimal = label === "xG" || label === "xA" || label === "Sense Impact Score";
  const dispA = isPPDA ? a : isDecimal ? Number(a).toFixed(2) : Math.round(a);
  const dispB = isPPDA ? b : isDecimal ? Number(b).toFixed(2) : Math.round(b);
  const cmpA = isPPDA ? a : Math.round(a);
  const cmpB = isPPDA ? b : Math.round(b);
  const aWins = lowerWins ? cmpA <= cmpB : cmpA >= cmpB;
  const bWins = lowerWins ? cmpB <= cmpA : cmpB >= cmpA;
  const total = Number(a) + Number(b);
  // Bar length is the team's share of the combined value (min 6% so tiny
  // values stay visible); bars grow outward from the center.
  const pct = (v) => (total > 0 ? Math.max((v / total) * 100, 6) : 0);

  return (
    <Box sx={{ mb: 1.75 }}>
      <Typography
        sx={{
          fontSize: "12px",
          color: "var(--text-2)",
          textAlign: "center",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" gap={1.25}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: "var(--text-1)",
            minWidth: 40,
            textAlign: "right",
          }}
        >
          {dispA}
        </Typography>

        {/* Left track — fill anchored to the center */}
        <Box
          sx={{
            flex: 1,
            height: 12,
            borderRadius: "999px",
            backgroundColor: "var(--surface-2)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: `${pct(a)}%`,
              borderRadius: "999px",
              backgroundColor: aWins ? TEAM_A_COLOR : "var(--border-1)",
            }}
          />
        </Box>

        {/* Right track — fill anchored to the center */}
        <Box
          sx={{
            flex: 1,
            height: 12,
            borderRadius: "999px",
            backgroundColor: "var(--surface-2)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${pct(b)}%`,
              borderRadius: "999px",
              backgroundColor: bWins ? TEAM_B_COLOR : "var(--border-1)",
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: "var(--text-1)",
            minWidth: 40,
          }}
        >
          {dispB}
        </Typography>
      </Stack>
    </Box>
  );
};

// ─── Per-team lineup table ───
// Title + column headers stay fixed; only the player list scrolls.
const TeamTable = ({ teamName, players, loading }) => (
  <Box
    sx={{
      flex: { xs: "0 0 auto", lg: 1 },
      minWidth: 0,
      backgroundColor: "var(--surface-1)",
      border: "1px solid var(--border-1)",
      borderRadius: "12px",
      p: 2,
      display: "flex",
      flexDirection: "column",
      height: { lg: "100%" },
      boxSizing: "border-box",
    }}
  >
    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "var(--text-1)", mb: 1.5 }}>
      {teamName}
    </Typography>

    {/* Header row */}
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: "6px",
        backgroundColor: "var(--surface-2)",
      }}
    >
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "var(--text-2)", width: 44 }}>
        #JR
      </Typography>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "var(--text-2)", flex: 1 }}>
        Player
      </Typography>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "var(--text-2)", width: 72, textAlign: "center" }}>
        Game Time
      </Typography>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "var(--text-2)", width: 64, textAlign: "center" }}>
        Position
      </Typography>
    </Stack>

    <Box sx={{ flex: 1, minHeight: 0, overflowY: { lg: "auto" } }}>
    {loading ? (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={22} sx={{ color: "rgba(232, 108, 0, 1)" }} />
      </Stack>
    ) : players.length === 0 ? (
      <Typography sx={{ fontSize: "12px", color: "var(--text-2)", textAlign: "center", py: 3 }}>
        No lineup available
      </Typography>
    ) : (
      players.map((p, i) => (
        <Stack
          key={p.id ?? i}
          direction="row"
          alignItems="center"
          sx={{
            px: 1,
            py: 1,
            borderBottom: i < players.length - 1 ? "1px solid var(--border-1)" : "none",
            // Players with no game time (didn't play) are dimmed out
            opacity: p.gameTime == null || p.gameTime === "" ? 0.3 : 1,
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "var(--text-1)", width: 44 }}>
            {p.jersey != null && p.jersey !== "" ? `#${p.jersey}` : "NA"}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={p.photo || undefined}
              sx={{ width: 30, height: 30, backgroundColor: "var(--surface-2)" }}
            >
              <Person sx={{ fontSize: 18, color: "var(--text-2)" }} />
            </Avatar>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--text-1)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {p.name}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "13px", color: "var(--text-2)", width: 72, textAlign: "center" }}>
            {p.gameTime != null && p.gameTime !== "" ? p.gameTime : "NA"}
          </Typography>
          <Box sx={{ width: 64, display: "flex", justifyContent: "center" }}>
            <Chip
              label={p.position}
              size="small"
              sx={{
                height: 22,
                fontSize: "10px",
                fontWeight: 700,
                borderRadius: "6px",
                backgroundColor: "var(--surface-2)",
                color: "var(--text-2)",
                border: "1px solid var(--border-1)",
              }}
            />
          </Box>
        </Stack>
      ))
    )}
    </Box>
  </Box>
);

const MatchSummary = ({ matchId, match, token, organizerId }) => {
  // ── Sense data (same source as TMS: matchDetails.match_sense_data) ──
  const matchSense = useMemo(() => {
    const msd = match?.match_sense_data;
    return msd?.data?.length > 0 ? msd : null;
  }, [match]);

  const teamASense = matchSense?.data?.find((i) => i.team_notation === "A");
  const teamBSense = matchSense?.data?.find((i) => i.team_notation === "B");

  // xG / xT / xA come from dedicated blocks, everything else from .analysis
  const teamAxG = teamASense?.xg_v2?.cum_xg ?? teamASense?.xg;
  const teamBxG = teamBSense?.xg_v2?.cum_xg ?? teamBSense?.xg;
  const teamAxT = teamASense?.sense_obv?.total_sense_obv;
  const teamBxT = teamBSense?.sense_obv?.total_sense_obv;
  const teamAxA = teamASense?.xa?.total_xa;
  const teamBxA = teamBSense?.xa?.total_xa;

  // Same attribute list + keys as TMS compareData
  const compareData = useMemo(() => {
    if (!teamASense || !teamBSense) return [];
    return [
      { name: "Possession %", key: "possession" },
      Boolean(teamAxG || teamBxG) && { name: "xG", key: "xg" },
      Boolean(teamAxT || teamBxT) && { name: "Sense Impact Score", key: "xt" },
      Boolean(teamAxA || teamBxA) && { name: "xA", key: "xa" },
      { name: "Total Shots", key: "total_shots" },
      { name: "Shots On Target", key: "shots_on_target" },
      { name: "Shot Conversion Rate %", key: "shot_conversion_rate" },
      { name: "Passes Completed", key: "passes_completed" },
      { name: "Chances Created", key: "chances_created" },
      { name: "Successful Tackles", key: "successful_tackles" },
      { name: "Successful Interceptions", key: "successful_interceptions" },
      { name: "Duels Won", key: "duels_won" },
      { name: "Total Saves", key: "total_saves" },
      { name: "Fouls", key: "total_fouls" },
      { name: "PPDA", key: "ppda" },
      { name: "Progressive Passes", key: "progressive_passes" },
      { name: "Field Tilt %", key: "field_tilt" },
      { name: "Avg Passing Chain Length", key: "avg_passing_chain_length" },
    ].filter(Boolean);
  }, [teamASense, teamBSense, teamAxG, teamBxG, teamAxT, teamBxT, teamAxA, teamBxA]);

  const statValue = (teamSense, xg, xt, xa, key) => {
    if (key === "xg") return xg;
    if (key === "xt") return xt;
    if (key === "xa") return xa;
    return teamSense?.analysis?.[key];
  };

  const eventsA = useMemo(() => specialActionData(matchSense, "A"), [matchSense]);
  const eventsB = useMemo(() => specialActionData(matchSense, "B"), [matchSense]);

  // Game time footer: `90'` plus extra time (seconds → minutes), hidden when
  // the sense payload has no total_extra_time field — same as TMS.
  const hideGameTime = !("total_extra_time" in (matchSense?.match_sense || {}));
  const extraTime = matchSense?.match_sense?.total_extra_time
    ? Math.floor(matchSense.match_sense.total_extra_time / 60)
    : 0;

  // Penalty shootout row (only when the match had a PK phase)
  const showTieBreaker = Boolean(
    matchSense?.match_sense?.PK_end_timestamp && matchSense?.match_sense?.PK_start_timestamp
  );
  const teamAPenalty = teamASense?.special_actions?.penalty_shootout?.count;
  const teamBPenalty = teamBSense?.special_actions?.penalty_shootout?.count;

  // ── Lineups ──
  const [playersA, setPlayersA] = useState([]);
  const [playersB, setPlayersB] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  useEffect(() => {
    const teamAId = match?.team_a_details?.id;
    const teamBId = match?.team_b_details?.id;
    if (!matchId || !token || (!teamAId && !teamBId)) return;
    let cancelled = false;
    setLoadingPlayers(true);

    const toRow = (p) => ({
      id: p?.id,
      jersey: p?.user_match_details?.jersey_number,
      name: `${p?.first_name || ""} ${p?.last_name || ""}`.trim(),
      photo: p?.blob_image_url,
      gameTime: p?.user_match_details?.game_time,
      position: positionAlias(p?.user_match_details?.preferred_position),
    });

    const playedInMatch = [3, 6, 7, 8, 9].includes(Number(match?.status));
    Promise.all([
      teamAId
        ? getPlayersRelatedToTeam({ teamId: teamAId, matchId, organizerId, playedInMatch, token })
        : Promise.resolve([]),
      teamBId
        ? getPlayersRelatedToTeam({ teamId: teamBId, matchId, organizerId, playedInMatch, token })
        : Promise.resolve([]),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        setPlayersA((a || []).map(toRow));
        setPlayersB((b || []).map(toRow));
      })
      .catch((err) => {
        console.error("[MatchSummary] lineup fetch failed:", err?.message);
        if (!cancelled) {
          setPlayersA([]);
          setPlayersB([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPlayers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [matchId, match, token, organizerId]);

  const teamAName = match?.team_a_details?.name || "Team A";
  const teamBName = match?.team_b_details?.name || "Team B";

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      gap={2}
      sx={{
        p: { xs: 1.5, md: 2.5 },
        // Fixed viewport on desktop: each card scrolls on its own. On mobile
        // the cards stack and this container scrolls them as one column.
        height: "100%",
        minHeight: 0,
        boxSizing: "border-box",
        overflowY: { xs: "auto", lg: "hidden" },
      }}
    >
      {/* ── Left: Scoreline + Overall Summary (scrolls as one card) ── */}
      <Box
        sx={{
          flex: { xs: "0 0 auto", lg: 1.2 },
          width: { xs: "100%", lg: "auto" },
          minWidth: 0,
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          borderRadius: "12px",
          p: 2.5,
          height: { lg: "100%" },
          overflowY: { lg: "auto" },
          boxSizing: "border-box",
        }}
      >
        {/* Scoreline */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "var(--text-1)" }}>
            Scoreline
          </Typography>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "var(--text-1)" }}>
              {match?.match_day ? new Date(match.match_day).toDateString() : ""}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
              {match?.tournament_name || match?.tournament_details?.name || ""}
            </Typography>
          </Box>
        </Stack>

        {!matchSense ? (
          <Typography sx={{ fontSize: "12px", color: "var(--text-2)", textAlign: "center", py: 4 }}>
            Match summary is not available yet.
          </Typography>
        ) : (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
              <Stack alignItems="center" gap={0.75} sx={{ width: 90 }}>
                <Avatar
                  src={match?.team_a_details?.logo_url || undefined}
                  sx={{ width: 40, height: 40, backgroundColor: "var(--surface-2)", color: "var(--text-1)" }}
                >
                  {(teamAName || "?")[0]}
                </Avatar>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-1)", textAlign: "center" }}>
                  {teamAName}
                </Typography>
              </Stack>

              <Stack alignItems="center" gap={0.75}>
                <Chip
                  label="Full Time"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#2f9e44",
                    backgroundColor: "rgba(47, 158, 68, 0.12)",
                    border: "1px solid rgba(47, 158, 68, 0.4)",
                  }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    px: 2.5,
                    py: 0.5,
                    borderRadius: "6px",
                    backgroundColor: "var(--surface-2)",
                    border: "1px solid var(--border-1)",
                  }}
                >
                  <Typography sx={{ fontSize: "18px", fontWeight: 800, color: "var(--text-1)" }}>
                    {teamASense?.analysis?.total_goals ?? "-"}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "var(--text-2)" }}>–</Typography>
                  <Typography sx={{ fontSize: "18px", fontWeight: 800, color: "var(--text-1)" }}>
                    {teamBSense?.analysis?.total_goals ?? "-"}
                  </Typography>
                </Stack>
                {showTieBreaker && teamAPenalty !== undefined && teamBPenalty !== undefined && (
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-2)" }}>
                    Penalties : {teamAPenalty} - {teamBPenalty}
                  </Typography>
                )}
                {!hideGameTime && (
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-2)" }}>
                    {`${match?.game_time ?? ""}'${extraTime === 0 ? "" : "+" + extraTime + "'"}`}
                  </Typography>
                )}
              </Stack>

              <Stack alignItems="center" gap={0.75} sx={{ width: 90 }}>
                <Avatar
                  src={match?.team_b_details?.logo_url || undefined}
                  sx={{ width: 40, height: 40, backgroundColor: "var(--surface-2)", color: "var(--text-1)" }}
                >
                  {(teamBName || "?")[0]}
                </Avatar>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-1)", textAlign: "center" }}>
                  {teamBName}
                </Typography>
              </Stack>
            </Stack>

            {/* Goal / card events */}
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2.5, gap: 2 }}>
              <Stack gap={0.75} sx={{ flex: 1 }}>
                {eventsA.map((e, i) => (
                  <Stack key={i} direction="row" alignItems="center" gap={0.75}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-2)", minWidth: 34 }}>
                      {e.time}'
                    </Typography>
                    <EventIcon type={e.type} />
                    <Typography sx={{ fontSize: "12px", color: "var(--text-1)" }}>{e.name}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack gap={0.75} sx={{ flex: 1, alignItems: "flex-end" }}>
                {eventsB.map((e, i) => (
                  <Stack key={i} direction="row" alignItems="center" gap={0.75}>
                    <Typography sx={{ fontSize: "12px", color: "var(--text-1)" }}>{e.name}</Typography>
                    <EventIcon type={e.type} />
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-2)", minWidth: 34, textAlign: "right" }}>
                      {e.time}'
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>

            {/* Legend */}
            <Stack
              direction="row"
              justifyContent="center"
              flexWrap="wrap"
              gap={2}
              sx={{ mt: 2, pt: 1.5, borderTop: "1px solid var(--border-1)" }}
            >
              {LEGEND.map((l) => (
                <Stack key={l.type} direction="row" alignItems="center" gap={0.5}>
                  <EventIcon type={l.type} />
                  <Typography sx={{ fontSize: "11px", color: "var(--text-2)" }}>{l.label}</Typography>
                </Stack>
              ))}
            </Stack>

            {/* Overall Summary */}
            {teamASense && teamBSense && (
              <>
                <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "var(--text-1)", mt: 3, mb: 2 }}>
                  Overall Summary
                </Typography>
                {compareData.map((stat) => (
                  <StatRow
                    key={stat.key}
                    label={stat.name}
                    a={statValue(teamASense, teamAxG, teamAxT, teamAxA, stat.key)}
                    b={statValue(teamBSense, teamBxG, teamBxT, teamBxA, stat.key)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Box>

      {/* ── Middle / Right: team lineup tables ── */}
      <TeamTable teamName={teamAName} players={playersA} loading={loadingPlayers} />
      <TeamTable teamName={teamBName} players={playersB} loading={loadingPlayers} />
    </Stack>
  );
};

export default MatchSummary;
